'use client'

import { useApi } from '@repo/api'
import { useAccess } from '@repo/auth'
import { permissions } from '@repo/permissions'
import type { GetCompanyTag } from '@repo/schema/company-tag'
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@repo/tanstack/query'
import { toast } from '@repo/ui/components'
import { isResponseError } from '@repo/upfetch'

import { COMPANY_LIST_FILTERS } from '../_utils/company-type'

/** The company lists' Company tags filter, sent as `filter[tags_in]`. */
export const COMPANY_TAGS_FILTER_ID = 'tags_in'

// Without view permission the API leaves `tags` out of company lists and answers the tag filter
// with 403, so every Company tag column, badge and filter hides.
export const useCompanyTagAccess = () => {
  const { allowed: canView } = useAccess({ permissions: [permissions.companies.tagTypesView] })
  const { allowed: canManage } = useAccess({ permissions: [permissions.companies.tagsManage] })
  return { canManage: canView && canManage, canView }
}

/**
 * The active Company tag types, sorted by the API; empty without view permission. `status` is
 * `success` once a catalogue has loaded, so a pending or failed one never reads as empty.
 */
export const useCompanyTagTypes = () => {
  const api = useApi()
  const { canView } = useCompanyTagAccess()
  const { data, status } = useQuery({
    ...api.companies.tagTypes.queryOptions(),
    enabled: canView,
    staleTime: 5 * 60 * 1000,
  })
  if (!canView) return { status: 'success' as const, tagTypes: [] }
  // A failed refetch keeps the catalogue it already has.
  return data ? { status: 'success' as const, tagTypes: data } : { status, tagTypes: [] }
}

const toastRequestError = (error: Error) => {
  // A request that never got a response, such as one made offline, still reports a failure.
  toast.error(isResponseError(error) ? error.message : 'Something went wrong. Please try again.')
}

/** A tag change request's input: a tag to add or remove, or a custom tag to create. */
type TagChange = { description?: null | string; name: string } | { tag_slug: string }

/** Takes back a tag change the field showed before its request failed. */
type TagChangeUndo = { added: (slug: string) => void; removed: (slug: string) => void }

// Each tag change's place in order, by its request's input, and each company's newest change.
const changeOrder = new WeakMap<TagChange, number>()
const newestChange = new Map<number, number>()

// Custom tags still being created, by company and typed name, and whether the field has since
// removed each one. The API removes a tag by its slug, which only creation's answer names.
const pendingCustomTags = new Map<string, { removed: boolean }>()
const pendingCustomKey = (companyId: number, name: string) => `${companyId}:${name}`

/**
 * Adds, removes and creates a company's tags. Each request answers with the company's whole tag
 * list, so a VIP / Blacklisted swap shows at once. Changes can overlap: only the newest change's
 * answer replaces the cached list, so an older answer that lands last never brings a tag back, and
 * once none is pending the list refetches in every scope. `isChanging` holds the field off the
 * cached list until then. Company lists and the history show tags too, so they refetch.
 */
export const useCompanyTagMutations = (companyId: number, undo: TagChangeUndo) => {
  const api = useApi()
  const queryClient = useQueryClient()
  const tagsKey = api.companies.tags.list.queryOptions({ path: { companyId } }).queryKey
  const mutationKey = ['company-tag-change', companyId]
  const isChanging = useIsMutating({ mutationKey }) > 0

  // Every company list, any page or filter, in every scope, and this company's history.
  const invalidates = [
    ...COMPANY_LIST_FILTERS.map(filter =>
      api.companies.listByType.allScopesKey({ input: {}, path: { filter } }),
    ),
    api.companies.history.list.allScopesKey({ input: {}, path: { id: companyId } }),
  ]

  // These run once per request, however many overlap; callbacks passed to `mutate` run for the
  // last call only.
  const startChange = (variables: TagChange) => {
    const order = (newestChange.get(companyId) ?? 0) + 1
    newestChange.set(companyId, order)
    changeOrder.set(variables, order)
  }
  const answerChange = (tags: GetCompanyTag[], variables: TagChange) => {
    if (changeOrder.get(variables) === newestChange.get(companyId)) {
      queryClient.setQueryData(tagsKey, tags)
    }
  }
  // The settling change still counts as pending, so 1 means it is the last.
  const settleChange = () => {
    if (queryClient.isMutating({ mutationKey }) > 1) return
    void queryClient.invalidateQueries({
      queryKey: api.companies.tags.list.allScopesKey({ path: { companyId } }),
    })
  }

  const failed = (error: Error, takeBack: () => void) => {
    takeBack()
    toastRequestError(error)
  }

  const addTag = useMutation({
    ...api.companies.tags.add.mutationOptions(
      { path: { companyId } },
      {
        meta: { invalidates },
        onError: (error, variables) => failed(error, () => undo.added(variables.tag_slug)),
        onMutate: startChange,
        onSettled: settleChange,
        onSuccess: answerChange,
      },
    ),
    mutationKey,
  })

  const removeTag = useMutation({
    ...api.companies.tags.remove.mutationOptions(
      { path: { companyId } },
      {
        meta: { invalidates },
        onError: (error, variables) => failed(error, () => undo.removed(variables.tag_slug)),
        onMutate: startChange,
        onSettled: settleChange,
        onSuccess: answerChange,
      },
    ),
    mutationKey,
  })

  // The field shows a new tag under its typed name until the answer names its slug. A removal
  // made meanwhile waits for that slug, and starts before this change settles, so the field
  // keeps off the answer's list until the removal's own answer.
  const createCustom = useMutation({
    ...api.companies.tags.createCustom.mutationOptions(
      { path: { companyId } },
      {
        meta: { invalidates: [...invalidates, api.companies.tagTypes.allScopesKey()] },
        onError: (error, variables) => {
          pendingCustomTags.delete(pendingCustomKey(companyId, variables.name))
          failed(error, () => undo.added(variables.name))
        },
        onMutate: variables => {
          startChange(variables)
          pendingCustomTags.set(pendingCustomKey(companyId, variables.name), { removed: false })
        },
        onSettled: settleChange,
        onSuccess: (tags, variables) => {
          answerChange(tags, variables)
          const key = pendingCustomKey(companyId, variables.name)
          const removed = pendingCustomTags.get(key)?.removed
          pendingCustomTags.delete(key)
          // The API reuses a type whose name matches, ignoring case.
          const name = variables.name.toLowerCase()
          const created = tags.find(tag => tag.tag_type.name.toLowerCase() === name)
          if (removed && created) removeTag.mutate({ tag_slug: created.tag_type.slug })
        },
      },
    ),
    mutationKey,
  })

  const createCustomTag = (name: string) => {
    const pending = pendingCustomTags.get(pendingCustomKey(companyId, name))
    // Picked again before its creation answered: the creation still tags the company.
    if (pending) {
      pending.removed = false
      return
    }
    createCustom.mutate({ name })
  }

  // `value` is a slug, or the typed name of a custom tag still being created.
  const removeCompanyTag = (value: string) => {
    const pending = pendingCustomTags.get(pendingCustomKey(companyId, value))
    if (pending) {
      pending.removed = true
      return
    }
    removeTag.mutate({ tag_slug: value })
  }

  return { addTag, createCustomTag, isChanging, removeTag: removeCompanyTag }
}

/** Archives a custom Company tag type, so it leaves the catalogue. Companies that have it keep it. */
export const useArchiveCompanyTagType = (slug: string) => {
  const api = useApi()
  return useMutation(
    api.companies.archiveTagType.mutationOptions(
      { path: { slug } },
      {
        meta: { invalidates: [api.companies.tagTypes.allScopesKey()] },
        onError: toastRequestError,
      },
    ),
  )
}
